// ===============================================================
// Forms — Public Form Page (NO auth required)
// Renders a published form for public submission at /forms/public/:slug
// ===============================================================

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader, Button } from '../../components/gab/GabComponents';
import { IconCheck, IconAlertCircle, IconClipboardList } from '@tabler/icons-react';
import { fetchTable, TABLES, FIELDS, createRecord } from '../../lib/api-switch';
import { FIELDS as RAW_FIELDS } from '../../api/fields';
import { FormRenderer } from './FormPreview';
import type { FormTemplate, FormSchema } from '../../types/forms';
import '../../styles/public-form.css';

// ── Helpers ─────────────────────────────────────────────────────

function parseTemplate(row: Record<string, unknown>): FormTemplate {
  const FT = FIELDS.formTemplates;
  const id = Number(row.record_id) || Number(row.id) || 0;
  const schema = String(row[FT.schema] ?? '{}');
  // isPublic/publicSlug stored inside schema JSON blob
  let isPublic = 'false';
  let publicSlug = '';
  try {
    const parsed = JSON.parse(schema);
    isPublic = (parsed.isPublic === true || parsed.isPublic === 'true') ? 'true' : 'false';
    publicSlug = String(parsed.publicSlug || '');
  } catch { /* ignore */ }
  return {
    id,
    Rid: id,
    templateName: String(row[FT.templateName] ?? ''),
    description: String(row[FT.description] ?? ''),
    schema,
    status: (String(row[FT.status] ?? 'draft')) as FormTemplate['status'],
    module: '',
    incidentId: '',
    isPublic,
    publicSlug,
    creatorName: String(row[FT.creatorName] ?? ''),
    responseCount: String(row[FT.responseCount] ?? '0'),
    datecreated: '',
  };
}

// ── Main Component ──────────────────────────────────────────────

export default function FormPublic() {
  const { slug } = useParams<{ slug: string }>();
  const [template, setTemplate] = useState<FormTemplate | null>(null);
  const [schema, setSchema] = useState<FormSchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        // Fetch all templates (no auth token — anonymous proxy injects admin token)
        const data = await fetchTable(TABLES.formTemplates, { limit: 200, anonymous: true });
        const templates = data.map(parseTemplate);
        const match = templates.find(t =>
          t.publicSlug === slug && t.isPublic === 'true' && t.status === 'published'
        );
        if (!match) {
          setError('not_found');
          return;
        }
        setTemplate(match);
        try {
          const parsed = JSON.parse(match.schema);
          // Ensure settings object exists with defaults
          if (parsed && !parsed.settings) {
            parsed.settings = {
              submitButtonText: 'Submit',
              successMessage: 'Thank you! Your response has been recorded.',
              allowMultipleSubmissions: false,
              notifyOnSubmission: false,
              notifyEmail: '',
            };
          }
          setSchema(parsed);
        } catch {
          setError('invalid_schema');
        }
      } catch (err) {
        setError('load_failed');
        console.error('Failed to load form:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  // ── Submit handler ──────────────────────────────────────────
  const handleSubmit = async (data: Record<string, unknown>) => {
    if (!template) return;
    setSubmitting(true);
    try {
      const respondentName = String(data.name || data.respondentName || data.full_name || data.f_full_name || data.f_first ? `${data.f_first || ''} ${data.f_last || ''}`.trim() : 'Anonymous');
      const respondentEmail = String(data.email || data.respondentEmail || data.f_email || '');
      const FR = RAW_FIELDS.formResponses;

      await createRecord(TABLES.formResponses, [
        [FR.templateId, String(template.Rid || template.id)],
        [FR.respondentName, respondentName],
        [FR.respondentEmail, respondentEmail],
        [FR.responseData, JSON.stringify(data)],
        [FR.status, 'new'],
        [FR.submittedAt, new Date().toISOString()],
      ], { anonymous: true });
      setSubmitted(true);
    } catch (err) {
      console.error('Submission failed:', err);
      alert('Failed to submit form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const topbar = (
    <div className="pf-topbar">
      <img src="/cds/assets/logo/OpenGov-Logo-Blurple.svg" alt="OpenGov" className="pf-topbar-logo" />
      <div className="pf-topbar-divider" />
      <span className="pf-topbar-product">Emergency Management</span>
    </div>
  );

  // ── Loading ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="pf-page">
        {topbar}
        <div className="pf-content pf-content--narrow">
          <div className="pf-card pf-state-wrap">
            <Loader size="lg" />
          </div>
        </div>
      </div>
    );
  }

  // ── Error states ────────────────────────────────────────────
  if (error) {
    return (
      <div className="pf-page">
        {topbar}
        <div className="pf-content pf-content--narrow">
          <div className="pf-card pf-state-wrap">
            <div className="pf-state-icon pf-state-icon--error">
              <IconAlertCircle size={30} />
            </div>
            <p className="pf-state-title">
              {error === 'not_found' ? 'Form Not Found' : 'Unable to Load Form'}
            </p>
            <p className="pf-state-sub">
              {error === 'not_found'
                ? 'This form does not exist, is no longer accepting responses, or the link may be incorrect.'
                : 'There was a problem loading this form. Please try again later.'}
            </p>
          </div>
        </div>
        <p className="pf-footer">Powered by OpenGov Emergency Management</p>
      </div>
    );
  }

  // ── Success ─────────────────────────────────────────────────
  if (submitted && schema) {
    return (
      <div className="pf-page">
        {topbar}
        <div className="pf-content pf-content--narrow">
          <div className="pf-card pf-state-wrap">
            <div className="pf-state-icon pf-state-icon--success">
              <IconCheck size={30} />
            </div>
            <p className="pf-state-title">Response Submitted</p>
            <p className="pf-state-sub">
              {schema.settings?.successMessage || 'Thank you! Your response has been recorded.'}
            </p>
            {schema.settings?.allowMultipleSubmissions && (
              <Button variant="light" onClick={() => setSubmitted(false)}>
                Submit Another Response
              </Button>
            )}
          </div>
        </div>
        <p className="pf-footer">Powered by OpenGov Emergency Management</p>
      </div>
    );
  }

  // ── Form ────────────────────────────────────────────────────
  if (!schema) return null;

  return (
    <div className="pf-page">
      {topbar}
      <div className="pf-content">
        <div className="pf-header-card">
          <div className="pf-header-icon">
            <IconClipboardList size={22} />
          </div>
          <div>
            <p className="pf-header-title">{template?.templateName || 'Form'}</p>
            {template?.description && (
              <p className="pf-header-sub">{template.description}</p>
            )}
          </div>
        </div>

        <div className="pf-card" style={{ position: 'relative' }}>
          {submitting && (
            <div style={{
              position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.85)',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', borderRadius: 4, zIndex: 10, gap: 8,
            }}>
              <Loader size="md" />
              <span style={{ fontSize: 14, color: '#6b7280' }}>Submitting…</span>
            </div>
          )}
          <FormRenderer schema={schema} onSubmit={handleSubmit} />
        </div>
      </div>
      <p className="pf-footer">Powered by OpenGov Emergency Management</p>
    </div>
  );
}
